import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import {
  GdRichTextarea,
  type GdRichTextInsertHandle,
} from "@/components/ui/GdRichTextarea";
import {
  DOKUMENT_TYP_LABELS,
  type DokumentDetail,
  type DokumentTyp,
} from "@/lib/dokumente";
import {
  fetchGdPlaceholderCatalog,
  type GdPlaceholderCategory,
} from "@/lib/gdPlaceholders";
import {
  createGdTextbaustein,
  deleteGdTextbaustein,
  listGdTextbausteine,
  updateGdTextbaustein,
  type GdTextbaustein,
  type GdTextbausteinBereich,
} from "@/lib/gdTextbausteine";
import {
  deleteGdStandardtext,
  listGdStandardtexte,
  saveGdStandardtext,
} from "@/lib/gdStandardtexte";
import { toast } from "@/lib/toast";
import { GdPlaceholderPicker } from "@/pages/dokumente/GdPlaceholderPicker";

export type BodyTextKind = "einleitung" | "schlusstext";

const TITLES: Record<BodyTextKind, string> = {
  einleitung: "Einleitungstext",
  schlusstext: "Schlusstext",
};

type SideTab = "bausteine" | "platzhalter";

interface DocumentBodyTextModalProps {
  open: boolean;
  kind: BodyTextKind | null;
  onClose: () => void;
  doc: DokumentDetail;
  editable: boolean;
  onChange: (patch: Partial<DokumentDetail>) => void;
  zIndexClass?: string;
}

export function DocumentBodyTextModal({
  open,
  kind,
  onClose,
  doc,
  editable,
  onChange,
  zIndexClass,
}: DocumentBodyTextModalProps) {
  const activeKind: BodyTextKind = kind ?? "einleitung";
  const value = activeKind === "einleitung" ? doc.einleitung || "" : doc.schlusstext || "";
  const dokumentTyp = (doc.dokument_typ || "angebot") as DokumentTyp;
  const typLabel = DOKUMENT_TYP_LABELS[dokumentTyp] || dokumentTyp;

  const mainEditorRef = useRef<GdRichTextInsertHandle>(null);
  const bausteinEditorRef = useRef<GdRichTextInsertHandle>(null);

  const [sideTab, setSideTab] = useState<SideTab>("bausteine");
  const [bausteine, setBausteine] = useState<GdTextbaustein[]>([]);
  const [loadingBausteine, setLoadingBausteine] = useState(false);
  const [categories, setCategories] = useState<GdPlaceholderCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftInhalt, setDraftInhalt] = useState("");
  const [draftBereich, setDraftBereich] = useState<GdTextbausteinBereich>("beide");
  const [creating, setCreating] = useState(false);
  const [savingBaustein, setSavingBaustein] = useState(false);
  const [savingAsBaustein, setSavingAsBaustein] = useState(false);
  const [quickName, setQuickName] = useState("");
  const [showQuickSave, setShowQuickSave] = useState(false);
  const [standardInhalt, setStandardInhalt] = useState<string | null>(null);
  const [savingStandard, setSavingStandard] = useState(false);

  const showBausteinEditor = creating || editingId != null;

  useEffect(() => {
    if (!open) return;
    setSideTab("bausteine");
    setEditingId(null);
    setCreating(false);
    setShowQuickSave(false);
    setQuickName("");
  }, [open, activeKind]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingBausteine(true);
    void listGdTextbausteine(activeKind)
      .then((rows) => {
        if (!cancelled) setBausteine(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setBausteine([]);
          toast.error("Textbausteine konnten nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingBausteine(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeKind]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchGdPlaceholderCatalog().then((cats) => {
      if (cancelled) return;
      setCategories(cats);
      setActiveCategory((prev) => prev || cats[0]?.key || "");
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listGdStandardtexte(dokumentTyp)
      .then((rows) => {
        if (cancelled) return;
        const match = rows.find((r) => r.bereich === activeKind);
        setStandardInhalt(match ? match.inhalt : null);
      })
      .catch(() => {
        if (!cancelled) setStandardInhalt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, dokumentTyp, activeKind]);

  function setValue(next: string) {
    if (activeKind === "einleitung") onChange({ einleitung: next });
    else onChange({ schlusstext: next });
  }

  function insertIntoActiveEditor(snippet: string, asBlock = false) {
    if (!editable) return;
    if (showBausteinEditor) {
      bausteinEditorRef.current?.insertText(snippet, { asBlock });
      return;
    }
    mainEditorRef.current?.insertText(snippet, { asBlock });
  }

  function startCreate() {
    setCreating(true);
    setEditingId(null);
    setDraftName("");
    setDraftInhalt("");
    setDraftBereich(activeKind);
    setSideTab("platzhalter");
  }

  function startEdit(item: GdTextbaustein) {
    setCreating(false);
    setEditingId(item.id);
    setDraftName(item.name);
    setDraftInhalt(item.inhalt);
    setDraftBereich(item.bereich);
    setSideTab("platzhalter");
  }

  function cancelEdit() {
    setCreating(false);
    setEditingId(null);
    setDraftName("");
    setDraftInhalt("");
    setSideTab("bausteine");
  }

  async function saveBaustein() {
    const name = draftName.trim();
    if (!name) {
      toast.error("Bitte einen Namen vergeben.");
      return;
    }
    setSavingBaustein(true);
    try {
      if (creating) {
        const created = await createGdTextbaustein({
          name,
          inhalt: draftInhalt,
          bereich: draftBereich,
          sort_index: bausteine.length,
        });
        setBausteine((prev) => [...prev, created]);
        toast.success("Textbaustein gespeichert.");
      } else if (editingId != null) {
        const updated = await updateGdTextbaustein(editingId, {
          name,
          inhalt: draftInhalt,
          bereich: draftBereich,
        });
        setBausteine((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
        toast.success("Textbaustein aktualisiert.");
      }
      cancelEdit();
    } catch {
      toast.error("Textbaustein konnte nicht gespeichert werden.");
    } finally {
      setSavingBaustein(false);
    }
  }

  async function removeBaustein(id: number) {
    if (!window.confirm("Textbaustein wirklich löschen?")) return;
    try {
      await deleteGdTextbaustein(id);
      setBausteine((prev) => prev.filter((b) => b.id !== id));
      if (editingId === id) cancelEdit();
      toast.success("Textbaustein gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  }

  function openQuickSave() {
    if (!value.trim()) {
      toast.error("Kein Text zum Speichern vorhanden.");
      return;
    }
    const firstLine = value
      .split(/\n/)
      .map((l) => l.trim())
      .find(Boolean);
    setQuickName(firstLine?.slice(0, 80) || TITLES[activeKind]);
    setShowQuickSave(true);
  }

  async function confirmQuickSave() {
    const name = quickName.trim();
    if (!name) {
      toast.error("Bitte einen Namen vergeben.");
      return;
    }
    setSavingAsBaustein(true);
    try {
      const created = await createGdTextbaustein({
        name,
        inhalt: value,
        bereich: activeKind,
        sort_index: bausteine.length,
      });
      setBausteine((prev) => [...prev, created]);
      setShowQuickSave(false);
      setSideTab("bausteine");
      toast.success("Als Textbaustein gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingAsBaustein(false);
    }
  }

  async function saveAsStandard() {
    setSavingStandard(true);
    try {
      const saved = await saveGdStandardtext(dokumentTyp, activeKind, value);
      setStandardInhalt(saved.inhalt);
      toast.success(`Standard-${TITLES[activeKind]} für ${typLabel} gespeichert.`);
    } catch {
      toast.error("Standardtext konnte nicht gespeichert werden.");
    } finally {
      setSavingStandard(false);
    }
  }

  async function loadStandard() {
    if (standardInhalt == null) {
      toast.error(`Kein Standardtext für ${typLabel} hinterlegt.`);
      return;
    }
    if (value.trim() && !window.confirm("Aktuellen Text durch Standardtext ersetzen?")) {
      return;
    }
    setValue(standardInhalt);
    toast.success("Standardtext geladen.");
  }

  async function clearStandard() {
    if (standardInhalt == null) return;
    if (!window.confirm(`Standard-${TITLES[activeKind]} für ${typLabel} löschen?`)) return;
    try {
      await deleteGdStandardtext(dokumentTyp, activeKind);
      setStandardInhalt(null);
      toast.success("Standardtext gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        showBausteinEditor
          ? creating
            ? "Neuer Textbaustein"
            : "Textbaustein bearbeiten"
          : TITLES[activeKind]
      }
      size="4xl"
      zIndexClass={zIndexClass}
      bodyClassName="!p-0"
    >
      <div className="grid min-h-[28rem] lg:grid-cols-[minmax(0,1.1fr)_minmax(22rem,0.9fr)]">
        <div className="flex min-h-0 flex-col border-b border-separator p-4 lg:border-b-0 lg:border-r">
          {showBausteinEditor ? (
            <>
              <p className="mb-3 text-sm text-label-secondary">
                Baustein bearbeiten. Platzhalter rechts werden an der Cursorposition eingefügt.
              </p>
              <div className="mb-3 space-y-2">
                <TextInput
                  label="Name"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  disabled={!editable || savingBaustein}
                />
                <label className="block space-y-1 text-sm">
                  <span className="text-label-secondary">Verfügbar für</span>
                  <select
                    className="eh-input w-full"
                    value={draftBereich}
                    onChange={(e) => setDraftBereich(e.target.value as GdTextbausteinBereich)}
                    disabled={!editable || savingBaustein}
                  >
                    <option value="beide">Einleitung & Schlusstext</option>
                    <option value="einleitung">Nur Einleitung</option>
                    <option value="schlusstext">Nur Schlusstext</option>
                  </select>
                </label>
              </div>
              <GdRichTextarea
                ref={bausteinEditorRef}
                value={draftInhalt}
                onChange={(e) => setDraftInhalt(e.target.value)}
                disabled={!editable || savingBaustein}
                minRows={10}
                aria-label="Textbaustein-Inhalt"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveBaustein()}
                  loading={savingBaustein}
                  disabled={!editable}
                >
                  Baustein speichern
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={cancelEdit}
                  disabled={savingBaustein}
                >
                  Abbrechen
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="mb-3 text-sm text-label-secondary">
                Text bearbeiten. Platzhalter und Bausteine werden an der Cursorposition eingefügt.
                Die Feldbreite entspricht dem Textblock auf dem Dokument.
              </p>
              <GdRichTextarea
                ref={mainEditorRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={!editable}
                minRows={10}
                variant="documentBody"
                aria-label={TITLES[activeKind]}
              />

              {editable ? (
                <div className="mt-3 space-y-2">
                  {showQuickSave ? (
                    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-separator bg-card p-2.5">
                      <div className="min-w-[12rem] flex-1">
                        <TextInput
                          label="Name für Textbaustein"
                          value={quickName}
                          onChange={(e) => setQuickName(e.target.value)}
                          disabled={savingAsBaustein}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void confirmQuickSave()}
                        loading={savingAsBaustein}
                      >
                        Speichern
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => setShowQuickSave(false)}
                        disabled={savingAsBaustein}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="secondary" onClick={openQuickSave}>
                        Als Baustein speichern
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => void saveAsStandard()}
                        loading={savingStandard}
                      >
                        Als Standard für {typLabel}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => void loadStandard()}
                        disabled={standardInhalt == null}
                      >
                        Standard laden
                      </Button>
                      {standardInhalt != null ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => void clearStandard()}
                        >
                          Standard löschen
                        </Button>
                      ) : null}
                    </div>
                  )}
                  <p className="text-xs text-label-tertiary">
                    {standardInhalt != null
                      ? `Standard-${TITLES[activeKind]} für ${typLabel} ist hinterlegt und wird bei neuen Dokumenten vorausgefüllt.`
                      : `Noch kein Standard-${TITLES[activeKind]} für ${typLabel}.`}
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex min-h-0 flex-col">
          <div className="flex border-b border-separator">
            <button
              type="button"
              className={clsx(
                "flex-1 px-3 py-2.5 text-sm font-medium transition",
                sideTab === "bausteine"
                  ? "border-b-2 border-label text-label"
                  : "text-label-secondary hover:text-label",
              )}
              onClick={() => setSideTab("bausteine")}
              disabled={showBausteinEditor}
            >
              Textbausteine
            </button>
            <button
              type="button"
              className={clsx(
                "flex-1 px-3 py-2.5 text-sm font-medium transition",
                sideTab === "platzhalter" || showBausteinEditor
                  ? "border-b-2 border-label text-label"
                  : "text-label-secondary hover:text-label",
              )}
              onClick={() => setSideTab("platzhalter")}
            >
              Platzhalter
            </button>
          </div>

          {showBausteinEditor || sideTab === "platzhalter" ? (
            <GdPlaceholderPicker
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              editable={editable}
              onInsert={(token) => insertIntoActiveEditor(token)}
            />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                  Gespeicherte Bausteine
                </p>
                {editable ? (
                  <Button type="button" size="sm" variant="secondary" onClick={startCreate}>
                    Neu
                  </Button>
                ) : null}
              </div>

              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
                {loadingBausteine ? (
                  <p className="text-sm text-label-secondary">Laden…</p>
                ) : bausteine.length === 0 ? (
                  <p className="text-sm text-label-secondary">
                    Noch keine Textbausteine. Speichere den aktuellen Text per Knopfdruck oder lege
                    einen neuen Baustein an.
                  </p>
                ) : (
                  bausteine.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg border border-separator bg-card p-2.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => insertIntoActiveEditor(item.inhalt, true)}
                          disabled={!editable}
                          title="An Cursorposition einfügen"
                        >
                          <p className="truncate text-sm font-medium text-label">{item.name}</p>
                          <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs text-label-secondary">
                            {item.inhalt || "—"}
                          </p>
                        </button>
                        {editable ? (
                          <div className="flex shrink-0 gap-1">
                            <button
                              type="button"
                              className="rounded px-1.5 py-0.5 text-xs text-accent hover:bg-card-elevated"
                              onClick={() => startEdit(item)}
                            >
                              Bearbeiten
                            </button>
                            <button
                              type="button"
                              className="rounded px-1.5 py-0.5 text-xs text-label-secondary hover:bg-card-elevated hover:text-red-600"
                              onClick={() => void removeBaustein(item.id)}
                            >
                              Löschen
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
